import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Wand2, Loader2, AlertCircle, Check, Film, ChevronDown, Sparkles, Clock, Image as ImageIcon, Zap } from 'lucide-react'
import { useProjectStore } from '../../stores/projectStore'

interface VideoGenerationDialogProps {
  open: boolean
  onClose: () => void
  sceneId: string | null
  projectId: string
}

type VideoGenerationMode = 'text-to-video' | 'image-to-video'

interface GenerationSettings {
  prompt: string
  negativePrompt: string
  duration: number // seconds
  aspectRatio: string
  numVideos: number
  seed?: number
  imageUrl?: string // For image-to-video
}

const ASPECT_RATIOS = [
  { label: '16:9 (Landscape)', value: '16:9' },
  { label: '9:16 (Portrait)', value: '9:16' },
  { label: '1:1 (Square)', value: '1:1' },
]

const DURATION_OPTIONS = [
  { label: '3 seconds', value: 3 },
  { label: '5 seconds', value: 5 },
  { label: '10 seconds', value: 10 },
]

const PROMPT_HELPERS = {
  motion: [
    'Slow pan left', 'Slow pan right', 'Zoom in', 'Zoom out', 'Tracking shot', 
    'Crane shot', 'Dolly shot', 'Steady cam', 'Orbiting', 'Push in'
  ],
  style: [
    'Cinematic', 'Realistic', 'Anime', '3D Animation', 'Stop motion', 
    'Documentary', 'Music video', 'Timelapse', 'Slow motion', 'Film noir'
  ],
  atmosphere: [
    'Dramatic lighting', 'Golden hour', 'Foggy', 'Rainy', 'Snowy', 
    'Sunset', 'Night scene', 'Neon lights', 'Volumetric lighting', 'High contrast'
  ]
}

const VideoGenerationDialog: React.FC<VideoGenerationDialogProps> = ({
  open,
  onClose,
  sceneId,
  projectId
}) => {
  const [mode, setMode] = useState<VideoGenerationMode>('text-to-video')
  const [settings, setSettings] = useState<GenerationSettings>({
    prompt: '',
    negativePrompt: '',
    duration: 5,
    aspectRatio: '16:9',
    numVideos: 1,
  })
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [activeHelperTab, setActiveHelperTab] = useState<keyof typeof PROMPT_HELPERS | null>(null)
  const [referenceImage, setReferenceImage] = useState<File | null>(null)
  const [referenceImagePreview, setReferenceImagePreview] = useState<string | null>(null)

  const { currentProject } = useProjectStore()

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setIsGenerating(false)
      setError(null)
      setSuccess(false)
      setReferenceImage(null)
      setReferenceImagePreview(null)
    }
  }, [open])

  // Esc key handler
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open && !isGenerating) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [open, isGenerating, onClose])

  const handlePromptHelperClick = (helper: string) => {
    setSettings(prev => ({
      ...prev,
      prompt: prev.prompt ? `${prev.prompt}, ${helper.toLowerCase()}` : helper.toLowerCase()
    }))
  }

  const handleReferenceImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setReferenceImage(file)
      const reader = new FileReader()
      reader.onload = (e) => {
        setReferenceImagePreview(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleGenerate = async () => {
    if (!settings.prompt.trim() || !sceneId) return

    setIsGenerating(true)
    setError(null)
    setSuccess(false)

    try {
      // For now, show a placeholder message since video generation models
      // would require specific API integration
      // In production, this would call fal.ai video generation models like:
      // - fal-ai/runway-gen3
      // - fal-ai/luma-dream-machine
      // - fal-ai/kling
      // - fal-ai/minimax
      
      // Simulate API call for demo purposes
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // TODO: Implement actual video generation API call
      // const result = await fal.subscribe('fal-ai/luma-dream-machine/ray', {
      //   input: {
      //     prompt: settings.prompt,
      //     aspect_ratio: settings.aspectRatio,
      //     duration: settings.duration,
      //   }
      // })

      setSuccess(true)
      setError('Video generation is coming soon! The infrastructure for AI video generation is being set up.')
      
    } catch (err) {
      console.error('Video generation failed:', err)
      setError(err instanceof Error ? err.message : 'Failed to generate video')
    } finally {
      setIsGenerating(false)
    }
  }

  if (!open) return null

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="glass-card w-full max-w-3xl max-h-[90vh] overflow-hidden border border-white/20"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500/30 to-purple-500/30 rounded-full flex items-center justify-center">
                <Film className="w-5 h-5 text-cyan-300" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Generate Video with AI</h2>
                <p className="text-white/60 text-sm">Create stunning videos from text or images</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="glass-button p-2 hover:bg-white/20"
              disabled={isGenerating}
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-180px)]">
            {!sceneId ? (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                <h3 className="text-white font-medium mb-2">No Scene Selected</h3>
                <p className="text-white/70">Please select a scene first to generate videos.</p>
              </div>
            ) : (
              <>
                {/* Mode Selection */}
                <div className="flex space-x-2">
                  <button
                    onClick={() => setMode('text-to-video')}
                    className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                      mode === 'text-to-video'
                        ? 'border-cyan-500 bg-cyan-500/10'
                        : 'border-white/20 hover:border-white/40'
                    }`}
                  >
                    <div className="flex items-center justify-center space-x-3">
                      <Sparkles className={`w-5 h-5 ${mode === 'text-to-video' ? 'text-cyan-400' : 'text-white/60'}`} />
                      <span className={mode === 'text-to-video' ? 'text-white' : 'text-white/70'}>Text to Video</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setMode('image-to-video')}
                    className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                      mode === 'image-to-video'
                        ? 'border-purple-500 bg-purple-500/10'
                        : 'border-white/20 hover:border-white/40'
                    }`}
                  >
                    <div className="flex items-center justify-center space-x-3">
                      <ImageIcon className={`w-5 h-5 ${mode === 'image-to-video' ? 'text-purple-400' : 'text-white/60'}`} />
                      <span className={mode === 'image-to-video' ? 'text-white' : 'text-white/70'}>Image to Video</span>
                    </div>
                  </button>
                </div>

                {/* Reference Image (for image-to-video mode) */}
                {mode === 'image-to-video' && (
                  <div className="space-y-2">
                    <label className="text-white/80 text-sm font-medium">Reference Image</label>
                    <div 
                      className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                        referenceImagePreview ? 'border-purple-500/50 bg-purple-500/5' : 'border-white/30 hover:border-white/50'
                      }`}
                    >
                      {referenceImagePreview ? (
                        <div className="relative">
                          <img 
                            src={referenceImagePreview} 
                            alt="Reference" 
                            className="max-h-40 mx-auto rounded-lg"
                          />
                          <button
                            onClick={() => {
                              setReferenceImage(null)
                              setReferenceImagePreview(null)
                            }}
                            className="absolute top-2 right-2 glass-button p-1 hover:bg-red-500/20"
                          >
                            <X className="w-4 h-4 text-white" />
                          </button>
                        </div>
                      ) : (
                        <label className="cursor-pointer block">
                          <ImageIcon className="w-10 h-10 text-white/40 mx-auto mb-2" />
                          <p className="text-white/70">Click to upload or drag an image</p>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleReferenceImageChange}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  </div>
                )}

                {/* Prompt */}
                <div className="space-y-2">
                  <label className="text-white/80 text-sm font-medium">Prompt</label>
                  <textarea
                    value={settings.prompt}
                    onChange={(e) => setSettings(prev => ({ ...prev, prompt: e.target.value }))}
                    placeholder="Describe the video you want to generate... e.g., 'A majestic eagle soaring over snow-capped mountains at golden hour, cinematic drone shot'"
                    className="glass-input w-full h-32 resize-none"
                  />
                  <p className="text-white/40 text-xs">{settings.prompt.length} characters</p>
                </div>

                {/* Prompt Helpers */}
                <div className="space-y-3">
                  <div className="flex space-x-2">
                    {Object.keys(PROMPT_HELPERS).map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setActiveHelperTab(activeHelperTab === tab as keyof typeof PROMPT_HELPERS ? null : tab as keyof typeof PROMPT_HELPERS)}
                        className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                          activeHelperTab === tab
                            ? 'bg-cyan-500/30 text-cyan-300'
                            : 'bg-white/5 text-white/60 hover:bg-white/10'
                        }`}
                      >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </button>
                    ))}
                  </div>

                  <AnimatePresence>
                    {activeHelperTab && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="flex flex-wrap gap-2"
                      >
                        {PROMPT_HELPERS[activeHelperTab].map((helper) => (
                          <button
                            key={helper}
                            onClick={() => handlePromptHelperClick(helper)}
                            className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg text-sm text-white/80 transition-colors"
                          >
                            {helper}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Settings Grid */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Aspect Ratio */}
                  <div className="space-y-2">
                    <label className="text-white/80 text-sm font-medium flex items-center space-x-1">
                      <span>Aspect Ratio</span>
                    </label>
                    <div className="relative">
                      <select
                        value={settings.aspectRatio}
                        onChange={(e) => setSettings(prev => ({ ...prev, aspectRatio: e.target.value }))}
                        className="glass-input w-full appearance-none pr-8"
                      >
                        {ASPECT_RATIOS.map((ratio) => (
                          <option key={ratio.value} value={ratio.value}>
                            {ratio.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                    </div>
                  </div>

                  {/* Duration */}
                  <div className="space-y-2">
                    <label className="text-white/80 text-sm font-medium flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>Duration</span>
                    </label>
                    <div className="relative">
                      <select
                        value={settings.duration}
                        onChange={(e) => setSettings(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                        className="glass-input w-full appearance-none pr-8"
                      >
                        {DURATION_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                    </div>
                  </div>
                </div>

                {/* Negative Prompt */}
                <div className="space-y-2">
                  <label className="text-white/80 text-sm font-medium">Negative Prompt (Optional)</label>
                  <textarea
                    value={settings.negativePrompt}
                    onChange={(e) => setSettings(prev => ({ ...prev, negativePrompt: e.target.value }))}
                    placeholder="What to avoid in the video... e.g., 'blurry, low quality, distorted, glitchy'"
                    className="glass-input w-full h-20 resize-none"
                  />
                </div>

                {/* Coming Soon Notice */}
                <div className="glass-card bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/20 p-4">
                  <div className="flex items-start space-x-3">
                    <Zap className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-white font-medium text-sm mb-1">AI Video Generation Coming Soon!</h4>
                      <p className="text-white/60 text-xs">
                        We're integrating cutting-edge video generation models including Runway Gen-3, 
                        Luma Dream Machine, and more. Stay tuned for updates!
                      </p>
                    </div>
                  </div>
                </div>

                {/* Error/Success Messages */}
                {error && (
                  <div className="glass-card bg-yellow-500/20 border border-yellow-500/30 p-4">
                    <div className="flex items-center space-x-2 text-yellow-200">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm">{error}</span>
                    </div>
                  </div>
                )}

                {success && !error && (
                  <div className="glass-card bg-green-500/20 border border-green-500/30 p-4">
                    <div className="flex items-center space-x-2 text-green-200">
                      <Check className="w-4 h-4" />
                      <span className="text-sm">Video generated successfully!</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          {sceneId && (
            <div className="flex justify-between p-6 border-t border-white/10">
              <button
                onClick={onClose}
                className="glass-button text-white/90 hover:bg-white/10 px-6 py-2"
                disabled={isGenerating}
              >
                Cancel
              </button>

              <button
                onClick={handleGenerate}
                disabled={!settings.prompt.trim() || isGenerating}
                className="glass-button bg-gradient-to-r from-cyan-500/30 to-purple-500/30 hover:from-cyan-500/40 hover:to-purple-500/40 text-white font-semibold px-6 py-2 disabled:opacity-50 flex items-center space-x-2"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4" />
                    <span>Generate Video</span>
                  </>
                )}
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default VideoGenerationDialog

